"use client";

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";

import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import {
  Printer,
  Loader2,
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
  Pill,
  FlaskConical,
  BadgeCheck,
  FileText,
  Minus,
  Plus,
  Trash2,
} from "lucide-react";

import "./DocumentPreview.css";

/* =========================================================
   HELPERS
========================================================= */

const pick = (...values) =>
  values.find(
    (value) =>
      value != null &&
      String(value).trim() !== ""
  ) || "";

/* =========================================================
   DOCUMENT CONFIG
========================================================= */

const DOCUMENT_CONFIG = {
  prescription: {
    label: "Prescription",
    icon: Pill,
    bodyTitle: "PRESCRIPTION MÉDICALE",
  },

  bilan: {
    label: "Lab / Test",
    icon: FlaskConical,
    bodyTitle: "DEMANDE D'ANALYSES / EXAMENS",
  },

  radio: {
    label: "Certificate",
    icon: BadgeCheck,
    bodyTitle: "CERTIFICAT MÉDICAL",
  },

  lettre: {
    label: "Referral Letter",
    icon: FileText,
    bodyTitle: "LETTRE DE RÉFÉRENCE",
  },
};

/* =========================================================
   PAGINATION CONFIG
========================================================= */

const MAX_CHARS_PER_LINE = 72;
const MAX_LINES_PER_PAGE = 17;

/* =========================================================
   WRAP TEXT
========================================================= */

const wrapTextToLines = (
  value,
  maxChars = MAX_CHARS_PER_LINE
) => {
  const text = String(value || "");

  if (!text.trim()) {
    return [""];
  }

  const originalLines = text.split("\n");
  const finalLines = [];

  originalLines.forEach((originalLine) => {
    const line = String(originalLine || "");

    if (!line.trim()) {
      finalLines.push("");
      return;
    }

    const words = line.split(/\s+/);

    let currentLine = "";

    words.forEach((word) => {
      const candidate = currentLine
        ? `${currentLine} ${word}`
        : word;

      if (
        candidate.length <= maxChars ||
        !currentLine
      ) {
        currentLine = candidate;
      } else {
        finalLines.push(currentLine);
        currentLine = word;
      }
    });

    if (currentLine) {
      finalLines.push(currentLine);
    }
  });

  return finalLines;
};

/* =========================================================
   CREATE PAGES
========================================================= */

const paginateContent = (value) => {
  const lines = wrapTextToLines(value);

  if (
    lines.length === 1 &&
    !lines[0]
  ) {
    return [""];
  }

  const pages = [];

  for (
    let index = 0;
    index < lines.length;
    index += MAX_LINES_PER_PAGE
  ) {
    pages.push(
      lines
        .slice(
          index,
          index + MAX_LINES_PER_PAGE
        )
        .join("\n")
    );
  }

  return pages.length
    ? pages
    : [""];
};

/* =========================================================
   DOCUMENT LINE CLASSIFICATION
========================================================= */

const MEDICATION_LINE_REGEX =
  /^(?:\d+\.\s*)?(.+?)(?:\s*—\s*(.+))?$/;

const classifyLine = (rawLine) => {
  const line = String(rawLine || "");
  const trimmed = line.trim();

  if (!trimmed) {
    return {
      type: "empty",
    };
  }

  if (
    trimmed.startsWith("•")
  ) {
    return {
      type: "bullet",
      text: trimmed.replace(
        /^•\s*/,
        ""
      ),
    };
  }

  const medicationMatch =
    trimmed.match(
      MEDICATION_LINE_REGEX
    );

  if (medicationMatch) {
    return {
      type: "medication",
      name: medicationMatch[1],
      dosage:
        medicationMatch[2] || "",
    };
  }

  return {
    type: "text",
    text: trimmed,
  };
};

/* =========================================================
   RENDER DOCUMENT LINE
========================================================= */

const renderDocumentLine = (
  rawLine,
  key
) => {
  const item =
    classifyLine(rawLine);

  if (
    item.type === "empty"
  ) {
    return (
      <div
        key={key}
        className="dp-line-spacer"
      >
        &nbsp;
      </div>
    );
  }

  if (
    item.type === "medication"
  ) {
    return (
      <div
        key={key}
        className="dp-med-line"
      >
        <span className="dp-med-name">
          {item.name}
        </span>

        {item.dosage && (
          <span className="dp-med-dosage">
            {item.dosage}
          </span>
        )}
      </div>
    );
  }

  if (
    item.type === "bullet"
  ) {
    return (
      <div
        key={key}
        className="dp-text-line"
      >
        {item.text}
      </div>
    );
  }

  return (
    <div
      key={key}
      className="dp-text-line"
    >
      {item.text}
    </div>
  );
};

/* =========================================================
   COMPONENT
========================================================= */

const DocumentPreview = ({
  prescriptionContent = "",
  docKey = "prescription",
  documentDraftTexts = {},

  generatedDocuments = [],
  activeGeneratedDocumentId = null,

  onActiveGeneratedDocumentChange,
  onGenerateDocument,

  /* ✅ DELETE */
  onDeleteGeneratedDocument,

  onPrintRecord,

  doctorData = null,
  layout = null,

  frontOnly = false,
  standalone = false,
}) => {
  const {
    t,
    i18n,
  } = useTranslation();

  /* =======================================================
     PAGE
  ======================================================= */

  const [
    currentPage,
    setCurrentPage,
  ] = useState(0);

  /* =======================================================
     FOLLOW UP
  ======================================================= */

  const [
    followUpAmount,
    setFollowUpAmount,
  ] = useState("1");

  const [
    followUpUnit,
    setFollowUpUnit,
  ] = useState("week");

  const [
    followUpCause,
    setFollowUpCause,
  ] = useState("");

  /* =======================================================
     PRINT
  ======================================================= */

  const [
    isGeneratingPdf,
    setIsGeneratingPdf,
  ] = useState(false);

  const [
    printState,
    setPrintState,
  ] = useState(null);

  const paperRef =
    useRef(null);

  /* =======================================================
     RTL
  ======================================================= */

  const isRTL = String(
    i18n.language || ""
  ).startsWith("ar");

  /* =======================================================
     DOCTOR DATA
  ======================================================= */

  const logoSrc =
    useMemo(
      () =>
        pick(
          layout?.logoUrl,
          layout?.logo,
          doctorData?.clinicLogo,
          doctorData?.logoUrl,
          ""
        ),
      [
        layout,
        doctorData,
      ]
    );

  const doctorName = pick(
    layout?.fullName,
    doctorData?.fullName,

    doctorData?.firstName ||
      doctorData?.lastName
      ? `${
          doctorData?.firstName ||
          ""
        } ${
          doctorData?.lastName ||
          ""
        }`.trim()
      : "",
  );

  const doctorSpecialty =
    pick(
      layout?.specialty,
      doctorData?.specialty,
      doctorData?.specialite,
      doctorData?.specialization,
      ""
    );

  const doctorNameAr =
    pick(
      layout?.fullNameAr,
      doctorData?.fullNameAr,
    );

  const doctorSpecialtyAr =
    pick(
      layout?.specialtyAr,
      doctorData?.specialtyAr,
    );

  const doctorPhone =
    pick(
      layout?.phone,
      doctorData?.phone,
      doctorData?.phoneNumber,
      ""
    );

  const doctorEmail =
    pick(
      layout?.email,
      doctorData?.email,
      ""
    );

  const doctorAddress =
    pick(
      layout?.address,
      doctorData?.clinicAddress,
      doctorData?.address,
      ""
    );

  const doctorAddressAr =
    pick(
      layout?.addressAr,
      doctorData?.addressAr,
      doctorAddress
    );

  /* =======================================================
     ACTIVE GENERATED DOCUMENT
  ======================================================= */

  const activeGeneratedDocument =
    useMemo(() => {
      return (
        generatedDocuments.find(
          (document) =>
            document.id === activeGeneratedDocumentId &&
            document.type === docKey
        ) || null
      );
    }, [
      generatedDocuments,
      activeGeneratedDocumentId,
      docKey,
    ]);

  /* =======================================================
     DRAFT
  ======================================================= */

  const draftDocument =
    useMemo(
      () => ({
        id: `draft-${docKey}`,

        type:
          docKey,

        title:
          t(`documentPreview.documentTypes.${docKey}`),

        content:
          prescriptionContent,

        isDraft: true,
      }),
      [
        docKey,
        prescriptionContent,
        t,
      ]
    );

  /* =======================================================
     ACTIVE DOCUMENT
  ======================================================= */

  const activeDocument =
    activeGeneratedDocument ||
    draftDocument;

  const activeType =
    activeDocument?.type ||
    "prescription";

  const activeConfig =
    DOCUMENT_CONFIG[
      activeType
    ] ||
    DOCUMENT_CONFIG.prescription;

  /* =======================================================
     PAGES
  ======================================================= */

  const pages =
    useMemo(
      () =>
        paginateContent(
          activeDocument?.content ||
            ""
        ),
      [
        activeDocument?.content,
      ]
    );

  const totalPages =
    pages.length;

  const hasMultiplePages =
    totalPages > 1;

  /* =======================================================
     PRINT DISPLAY
  ======================================================= */

  const displayDocument =
    printState?.document ||
    activeDocument;

  const displayPages =
    printState?.pages ||
    pages;

  const displayPage =
    printState
      ? printState.page
      : currentPage;

  const displayTotalPages =
    displayPages.length;

  const displayContent =
    displayPages[
      displayPage
    ] || "";

  const displayHasMultiplePages =
    displayTotalPages > 1;

  const displayConfig =
    DOCUMENT_CONFIG[
      displayDocument?.type
    ] ||
    DOCUMENT_CONFIG.prescription;
  const localizedDocumentLabel = t(
    `documentPreview.documentTypes.${displayDocument?.type || "prescription"}`
  );

  /* =======================================================
     REMOVE DUPLICATE DOCUMENT TITLE
  ======================================================= */

  const displayLines =
    useMemo(() => {
      const lines =
        displayContent.split(
          "\n"
        );

      const firstLine =
        (
          lines[0] || ""
        )
          .trim()
          .toLowerCase();

      if (
        firstLine ===
        localizedDocumentLabel.toLowerCase()
      ) {
        return lines.slice(
          1
        );
      }

      return lines;
    }, [
      displayContent,
      localizedDocumentLabel,
    ]);

  /* =======================================================
     RESET PAGE WHEN DOCUMENT CHANGES
  ======================================================= */

  useEffect(() => {
    setCurrentPage(0);
  }, [
    activeDocument?.id,
  ]);

  /* =======================================================
     PAGE SECURITY
  ======================================================= */

  useEffect(() => {
    if (
      currentPage >
      totalPages - 1
    ) {
      setCurrentPage(
        Math.max(
          0,
          totalPages - 1
        )
      );
    }
  }, [
    currentPage,
    totalPages,
  ]);

  /* =======================================================
     PAGE NAVIGATION
  ======================================================= */

  const goToFirstPage =
    () =>
      setCurrentPage(0);

  const goToPreviousPage =
    () =>
      setCurrentPage(
        (previous) =>
          Math.max(
            0,
            previous - 1
          )
      );

  const goToNextPage =
    () =>
      setCurrentPage(
        (previous) =>
          Math.min(
            totalPages - 1,
            previous + 1
          )
      );

  const goToLastPage =
    () =>
      setCurrentPage(
        Math.max(
          0,
          totalPages - 1
        )
      );

  /* =======================================================
     SELECT GENERATED DOCUMENT
  ======================================================= */

  const handleSelectGeneratedDocument =
    (document) => {
      onActiveGeneratedDocumentChange?.(
        document.id
      );

      setCurrentPage(0);
    };

  /* =======================================================
     ✅ DELETE GENERATED DOCUMENT
  ======================================================= */

  const handleDeleteGeneratedDocument =
    () => {
      if (
        !activeGeneratedDocument
      ) {
        return;
      }

      const confirmDelete =
        window.confirm(
          t("documentPreview.confirmDelete", {
            document: t(`documentPreview.documentTypes.${activeType}`),
          })
        );

      if (
        !confirmDelete
      ) {
        return;
      }

      onDeleteGeneratedDocument?.(
        activeGeneratedDocument.id
      );

      setCurrentPage(0);
    };

  /* =======================================================
     WAIT DOM
  ======================================================= */

  const waitForNextFrame =
    () =>
      new Promise(
        (resolve) =>
          requestAnimationFrame(
            resolve
          )
      );

  /* =======================================================
     PRINT ALL GENERATED DOCUMENTS
  ======================================================= */

  const handlePrint =
    async () => {
      const selectedDocument = generatedDocuments.find(
        (document) => document.type === docKey
      );

      if (!selectedDocument) {
        console.warn(
          "Generate at least one document before printing."
        );

        return;
      }

      if (
        isGeneratingPdf
      ) {
        return;
      }

      const paperElement =
        paperRef.current;

      if (
        !paperElement
      ) {
        return;
      }

      setIsGeneratingPdf(
        true
      );

      const a5PortraitWidthPx = (148 / 25.4) * 96;
      const a5PortraitHeightPx = (210 / 25.4) * 96;
      const originalPaperStyles = {
        width: paperElement.style.width,
        height: paperElement.style.height,
        minHeight: paperElement.style.minHeight,
        flex: paperElement.style.flex,
      };
      const previewRoot = paperElement.closest(".dp-figma");
      const originalRootStyles = previewRoot
        ? {
            height: previewRoot.style.height,
            overflow: previewRoot.style.overflow,
          }
        : null;

      try {
        paperElement.style.width = `${a5PortraitWidthPx}px`;
        paperElement.style.height = `${a5PortraitHeightPx}px`;
        paperElement.style.minHeight = `${a5PortraitHeightPx}px`;
        paperElement.style.flex = "none";
        if (previewRoot) {
          previewRoot.style.height = "auto";
          previewRoot.style.overflow = "visible";
        }

        const pdf =
          new jsPDF({
            orientation:
              "landscape",

            unit:
              "mm",

            format: [210, 148],
          });

        const pageWidth =
          pdf.internal.pageSize.getWidth();

        const pageHeight =
          pdf.internal.pageSize.getHeight();

        let isFirstRenderedPage =
          true;

        for (
          const document of [selectedDocument]
        ) {
          const documentPages =
            paginateContent(
              document?.content ||
                ""
            );

          for (
            let pageIndex = 0;
            pageIndex <
            documentPages.length;
            pageIndex += 1
          ) {
            setPrintState({
              document,

              pages:
                documentPages,

              page:
                pageIndex,
            });

            await waitForNextFrame();

            await waitForNextFrame();

            const canvas =
              await html2canvas(
                paperElement,
                {
                  scale: 3,

                  useCORS: true,

                  backgroundColor:
                    "#ffffff",
                }
              );

            const rotatedCanvas = paperElement.ownerDocument.createElement("canvas");
            rotatedCanvas.width = canvas.height;
            rotatedCanvas.height = canvas.width;
            const context = rotatedCanvas.getContext("2d");
            if (!context) throw new Error("Impossible de préparer la page PDF.");
            context.translate(rotatedCanvas.width, 0);
            context.rotate(Math.PI / 2);
            context.drawImage(canvas, 0, 0);

            const imageData =
              rotatedCanvas.toDataURL(
                "image/png"
              );

            if (
              !isFirstRenderedPage
            ) {
              pdf.addPage();
            }

            pdf.addImage(
              imageData,
              "PNG",
              0,
              0,
              pageWidth,
              pageHeight
            );

            isFirstRenderedPage =
              false;
          }
        }

        setPrintState(null);

        const fileName =
          `Documents-${Date.now()}.pdf`;

        const blobUrl =
          pdf.output(
            "bloburl"
          );

        const printWindow =
          window.open(
            blobUrl,
            "_blank"
          );

        if (
          printWindow
        ) {
          printWindow.addEventListener(
            "load",
            () => {
              printWindow.focus();

              printWindow.print();
            }
          );
        } else {
          pdf.save(
            fileName
          );
        }

        onPrintRecord?.(
          [selectedDocument]
        );
      } catch (
        error
      ) {
        console.error(
          "Erreur génération PDF :",
          error
        );
      } finally {
        paperElement.style.width = originalPaperStyles.width;
        paperElement.style.height = originalPaperStyles.height;
        paperElement.style.minHeight = originalPaperStyles.minHeight;
        paperElement.style.flex = originalPaperStyles.flex;
        if (previewRoot && originalRootStyles) {
          previewRoot.style.height = originalRootStyles.height;
          previewRoot.style.overflow = originalRootStyles.overflow;
        }
        setPrintState(
          null
        );

        setIsGeneratingPdf(
          false
        );
      }
    };

  const handleGenerateOrPrint = () => {
    const content = String(
      documentDraftTexts?.[docKey] ||
        (docKey === "prescription" ? prescriptionContent : "")
    ).trim();
    const generatedDocument = generatedDocuments.find(
      (document) => document.type === docKey
    );

    if (content && content !== String(generatedDocument?.content || "").trim()) {
      onGenerateDocument?.({
        type: docKey,
        title: t(`documentPreview.documentTypes.${docKey}`),
        content,
      });
      return;
    }

    handlePrint();
  };

  const selectedDraftContent = String(
    documentDraftTexts?.[docKey] ||
      (docKey === "prescription" ? prescriptionContent : "")
  ).trim();
  const selectedGeneratedDocument = generatedDocuments.find(
    (document) => document.type === docKey
  );
  const hasPendingSelectedDocument = Boolean(
    selectedDraftContent &&
    selectedDraftContent !== String(selectedGeneratedDocument?.content || "").trim()
  );

  /* =======================================================
     FOLLOW-UP AMOUNT
  ======================================================= */

  const handleFollowUpAmountChange =
    (event) => {
      const value =
        event.target.value;

      if (
        value === ""
      ) {
        setFollowUpAmount(
          ""
        );

        return;
      }

      const numericValue =
        Number(value);

      if (
        !Number.isFinite(
          numericValue
        )
      ) {
        return;
      }

      const normalizedValue =
        Math.min(
          99,
          Math.max(
            1,
            Math.floor(
              numericValue
            )
          )
        );

      setFollowUpAmount(
        String(
          normalizedValue
        )
      );
    };

  const handleFollowUpAmountBlur =
    () => {
      const numericValue =
        Number(
          followUpAmount
        );

      if (
        !Number.isFinite(
          numericValue
        ) ||
        numericValue < 1
      ) {
        setFollowUpAmount(
          "1"
        );
      }
    };

  const decreaseFollowUp =
    () => {
      const current =
        Math.max(
          1,
          Number(
            followUpAmount
          ) || 1
        );

      setFollowUpAmount(
        String(
          Math.max(
            1,
            current - 1
          )
        )
      );
    };

  const increaseFollowUp =
    () => {
      const current =
        Math.max(
          1,
          Number(
            followUpAmount
          ) || 1
        );

      setFollowUpAmount(
        String(
          Math.min(
            99,
            current + 1
          )
        )
      );
    };

  /* =======================================================
     FOLLOW UP LABEL
  ======================================================= */

  const followUpLabel =
    useMemo(() => {
      const amount =
        Math.max(
          1,
          Number(
            followUpAmount
          ) || 1
        );

      if (
        followUpUnit ===
        "week"
      ) {
        return `${amount} ${
          amount === 1
            ? "Week"
            : "Weeks"
        }`;
      }

      return `${amount} ${
        amount === 1
          ? "Month"
          : "Months"
      }`;
    }, [
      followUpAmount,
      followUpUnit,
    ]);

  /* =======================================================
     SCHEDULE
  ======================================================= */

  const handleScheduleFollowUp =
    () => {
      const amount =
        Math.max(
          1,
          Number(
            followUpAmount
          ) || 1
        );

      const payload = {
        amount,

        unit:
          followUpUnit,

        label:
          followUpLabel,

        cause:
          followUpCause.trim(),

        frontOnly,
      };

      console.log(
        "📅 FOLLOW-UP FRONT ONLY:",
        payload
      );
    };

  /* =======================================================
     UI
  ======================================================= */

  return (
    <div
      className={`dp-figma${standalone ? " dp-standalone" : ""}`}
      dir={
        isRTL
          ? "rtl"
          : "ltr"
      }
    >
      {/* =================================================
          PREVIEW
      ================================================= */}

      <section className="dp-preview-card">

        {/* HEADER */}

        <div className="dp-preview-heading">
          <div className="dp-preview-heading-text">

            <h3>
              {t(
                "documentPreview.title",
                {
                  defaultValue:
                    "Document preview",
                }
              )}
            </h3>

            <p>
              {activeGeneratedDocument
                ? t("documentPreview.generatedDocument", {
                    document: t(`documentPreview.documentTypes.${activeType}`),
                  })
                : t("documentPreview.reviewBeforeGenerating")}
            </p>
          </div>
        </div>

        {/* GENERATED DOCUMENTS + PAGINATION */}

        <div className="dp-generated-row">

          <div className="dp-generated-documents">

            {generatedDocuments.map(
              (document) => {
                const config =
                  DOCUMENT_CONFIG[
                    document.type
                  ] ||
                  DOCUMENT_CONFIG.prescription;

                const Icon =
                  config.icon;

                const active =
                  activeGeneratedDocumentId ===
                  document.id;

                return (
                  <button
                    key={
                      document.id
                    }
                    type="button"
                    className={`dp-generated-document-btn ${
                      active
                        ? "active"
                        : ""
                    }`}
                    onClick={() =>
                      handleSelectGeneratedDocument(
                        document
                      )
                    }
                  >
                    <Icon
                      size={11}
                      strokeWidth={
                        1.8
                      }
                    />

                    <span>
                      {t(`documentPreview.documentTypes.${document.type}`)}
                    </span>
                  </button>
                );
              }
            )}
          </div>

          {hasMultiplePages && (
            <div className="dp-pagination">

              <button
                type="button"
                className="dp-page-button"
                onClick={
                  goToFirstPage
                }
                disabled={
                  currentPage === 0
                }
                title={t("documentPreview.firstPage")}
                aria-label={t("documentPreview.firstPage")}
              >
                <ChevronsLeft
                  size={13}
                />
              </button>

              <button
                type="button"
                className="dp-page-button"
                onClick={
                  goToPreviousPage
                }
                disabled={
                  currentPage === 0
                }
                title={t("documentPreview.previousPage")}
                aria-label={t("documentPreview.previousPage")}
              >
                <ChevronLeft
                  size={13}
                />
              </button>

              <span className="dp-page-counter">
                {currentPage +
                  1}
                {" / "}
                {totalPages}
              </span>

              <button
                type="button"
                className="dp-page-button"
                onClick={
                  goToNextPage
                }
                disabled={
                  currentPage ===
                  totalPages - 1
                }
                title={t("documentPreview.nextPage")}
                aria-label={t("documentPreview.nextPage")}
              >
                <ChevronRight
                  size={13}
                />
              </button>

              <button
                type="button"
                className="dp-page-button"
                onClick={
                  goToLastPage
                }
                disabled={
                  currentPage ===
                  totalPages - 1
                }
                title={t("documentPreview.lastPage")}
                aria-label={t("documentPreview.lastPage")}
              >
                <ChevronsRight
                  size={13}
                />
              </button>
            </div>
          )}
        </div>

        {/* =================================================
            PAPER
        ================================================= */}

        <div
          className="dp-paper"
          ref={paperRef}
        >

          {/* ✅ DELETE DOCUMENT */}

          {activeGeneratedDocument &&
            !printState &&
            !standalone && (
              <button
                type="button"
                className="dp-delete-document-button"
                onClick={
                  handleDeleteGeneratedDocument
                }
                title={t("documentPreview.deleteDocument", {
                  document: t(`documentPreview.documentTypes.${activeType}`),
                })}
                aria-label={t("documentPreview.deleteDocument", {
                  document: t(`documentPreview.documentTypes.${activeType}`),
                })}
              >
                <Trash2
                  size={14}
                  strokeWidth={
                    1.8
                  }
                />
              </button>
            )}

          {/* HEADER */}

          <div className="dp-paper-header">

            <div className="dp-doctor-left">

              <div className="dp-doctor-logo">
                <img
                  src={
                    logoSrc
                  }
                  alt="Clinic"
                />
              </div>

              <div className="dp-doctor-data">
                <strong>
                  {
                    doctorName
                  }
                </strong>

                <span>
                  {
                    doctorSpecialty
                  }
                </span>
              </div>
            </div>

            <div
              className="dp-doctor-right"
              dir="rtl"
            >
              <strong>
                {
                  doctorNameAr
                }
              </strong>

              <span>
                {
                  doctorSpecialtyAr
                }
              </span>
            </div>
          </div>

          {/* BODY */}

          <div className="dp-paper-body">

            {displayContent && (
              <div className="dp-paper-text">

                {displayLines.map(
                  (
                    line,
                    index
                  ) =>
                    renderDocumentLine(
                      line,

                      `${displayDocument?.id}-${displayPage}-${index}`
                    )
                )}
              </div>
            )}
          </div>

          {/* PAGE NUMBER */}

          {displayHasMultiplePages && (
            <div className="dp-paper-page-number">
              {t("documentPreview.page")} {" "}
              {displayPage +
                1}
              {" / "}
              {
                displayTotalPages
              }
            </div>
          )}

          {/* FOOTER */}

          <div className="dp-paper-footer">

            <div className="dp-contact dp-contact-left">

              <div>
                <span>
                  ☎
                </span>

                {t("documentPreview.phone")}:{" "}
                {
                  doctorPhone
                }
              </div>

              <div>
                <span>
                  ✉
                </span>

                {t("documentPreview.email")}:{" "}
                {
                  doctorEmail
                }
              </div>

              <div>
                <span>
                  ⌖
                </span>

                {t("documentPreview.address")}:{" "}
                {
                  doctorAddress
                }
              </div>
            </div>

            <div
              className="dp-contact dp-contact-right"
              dir="rtl"
            >
              <div>
                الهاتف:{" "}
                {
                  doctorPhone
                }
              </div>

              <div>
                البريد الإلكتروني:{" "}
                {
                  doctorEmail
                }
              </div>

              <div>
                العنوان:{" "}
                {
                  doctorAddressAr
                }
              </div>
            </div>
          </div>
        </div>

        {/* PRINT */}

        <button
          type="button"
          className="dp-print-button"
          onClick={handleGenerateOrPrint}
          disabled={
            (!selectedGeneratedDocument && !selectedDraftContent) ||
            isGeneratingPdf
          }
        >
          {isGeneratingPdf ? (
            <Loader2
              size={14}
              strokeWidth={
                1.5
              }
              className="dp-spin"
            />
          ) : (
            <Printer
              size={14}
              strokeWidth={
                1.5
              }
            />
          )}

          <span>
            {hasPendingSelectedDocument
              ? t("documentPreview.generateSelected", {
                  document: t(`documentPreview.documentTypes.${docKey}`),
                })
              : isGeneratingPdf
              ? t("documentPreview.generatingPdf")
              : t("documentPreview.printSelected", {
                  document: t(`documentPreview.documentTypes.${docKey}`),
                })}
          </span>
        </button>
      </section>

      {/* =================================================
          FOLLOW UP
      ================================================= */}

      <section className="dp-followup-card">

        <div className="dp-followup-heading">
          <h3>
            {t("documentPreview.followUp.title")}
          </h3>

          <p>
            {t("documentPreview.followUp.subtitle")}
          </p>
        </div>

        <div className="dp-followup-editor">

          {/* NUMBER */}

          <div className="dp-followup-number">

            <button
              type="button"
              className="dp-followup-step"
              onClick={
                decreaseFollowUp
              }
              disabled={
                Number(
                  followUpAmount ||
                    1
                ) <= 1
              }
            >
              <Minus
                size={13}
              />
            </button>

            <input
              type="number"
              min="1"
              max="99"
              step="1"
              value={
                followUpAmount
              }
              onChange={
                handleFollowUpAmountChange
              }
              onBlur={
                handleFollowUpAmountBlur
              }
            />

            <button
              type="button"
              className="dp-followup-step"
              onClick={
                increaseFollowUp
              }
              disabled={
                Number(
                  followUpAmount ||
                    1
                ) >= 99
              }
            >
              <Plus
                size={13}
              />
            </button>
          </div>

          {/* UNIT */}

          <div className="dp-followup-units">

            <button
              type="button"
              className={
                followUpUnit ===
                "week"
                  ? "active"
                  : ""
              }
              onClick={() =>
                setFollowUpUnit(
                  "week"
                )
              }
            >
              {t("documentPreview.followUp.weeks")}
            </button>

            <button
              type="button"
              className={
                followUpUnit ===
                "month"
                  ? "active"
                  : ""
              }
              onClick={() =>
                setFollowUpUnit(
                  "month"
                )
              }
            >
              {t("documentPreview.followUp.months")}
            </button>
          </div>

          {/* RESULT */}

          <div className="dp-followup-result">
            {
              followUpLabel
            }
          </div>
        </div>

        {/* CAUSE */}

        <div className="dp-followup-form">

          <div className="dp-followup-field">

            <label>
              {t("documentPreview.followUp.cause")}
            </label>

            <input
              type="text"
              value={
                followUpCause
              }
              onChange={(
                event
              ) =>
                setFollowUpCause(
                  event.target
                    .value
                )
              }
              placeholder={t("documentPreview.followUp.causePlaceholder")}
            />
          </div>

          <button
            type="button"
            className="dp-schedule-button"
            onClick={
              handleScheduleFollowUp
            }
          >
            {t("documentPreview.followUp.schedule")}
          </button>
        </div>
      </section>
    </div>
  );
};

export default DocumentPreview;